import { useState } from "react";
import { Header } from "../../components/Header";
import { Stepper } from "../../components/Stepper";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  Input,
  Show
} from "shared/components";
import { ApplicantType } from "shared/types";
import { toast } from "react-toastify";
import { ManualOption } from "../../types";
import { SelfServeOption } from "../../types";
import { ApplicantToAddRow } from "./components/ApplicantToAddRow";
import { RoleSelect } from "./components/RoleSelect";
import { InvitationRadioType } from "./components/InvitationRadioType";
import { validateForms } from "./validations";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useGetInviteTemplateAndUnit } from "./hooks";
import { getErrorMessage } from "utils/error";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

export const InviteApplicants = ({
  setOpen,
  handleInvite,
  isInviteLoading,
  unitSelectSkipped,
  applicationId,
  propertyId,
  unitId,
  disabledRoles: _disabledRoles
}: {
  setOpen: (open: boolean) => void;
  handleInvite: (options: (SelfServeOption | ManualOption)[]) => void;
  isInviteLoading: boolean;
  unitSelectSkipped: boolean;
  applicationId?: number;
  propertyId?: number;
  unitId?: number;
  disabledRoles?: ApplicantType[];
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { template, unit } = useGetInviteTemplateAndUnit({
    applicationId,
    propertyId,
    unitId
  });
  const canCreateManual = useFeatureFlagEnabled("manual-applications-enabled");

  const [optionToAdd, setOptionToAdd] = useState<
    SelfServeOption | ManualOption
  >({
    type: "self-serve",
    role: ApplicantType.Applicant
  });

  const [options, setOptions] = useState<(ManualOption | SelfServeOption)[]>(
    []
  );

  const disabledRoles =
    _disabledRoles ||
    (options.some((option) =>
      [ApplicantType.Applicant, ApplicantType.Occupant].includes(option.role)
    )
      ? []
      : [ApplicantType.CoSigner]);

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(unit?.magic_link?.application_link);
      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <>
      <Header
        title="Add applicants"
        description="You can add several people to the unit"
        onClose={() => setOpen(false)}
      />

      <Hide if={unitSelectSkipped}>
        <div className="p-6 border-b border-alpha-40">
          <Stepper currentStepIdx={1} totalSteps={2} />
        </div>
      </Hide>

      <div className="space-y-5 p-6">
        <InvitationRadioType
          type="self-serve"
          title="Self-serve"
          description="Invite a person via email to complete the application"
          checked={optionToAdd.type === "self-serve"}
          onChange={() =>
            setOptionToAdd({
              type: "self-serve",
              role: ApplicantType.Applicant
            })
          }
        />
        <Show if={canCreateManual}>
          <InvitationRadioType
            type="manual"
            title="Manual"
            description="Manually fill in the person's information to run the screening services"
            checked={optionToAdd.type === "manual"}
            onChange={() =>
              setOptionToAdd({
                type: "manual",
                role: ApplicantType.Applicant
              })
            }
          />
        </Show>

        <form
          onSubmit={(e) => {
            e.preventDefault();

            const errors = validateForms(optionToAdd, options);
            if (errors) {
              toast.error(errors);
              return;
            }

            setOptions([...options, optionToAdd]);
            e.currentTarget.reset();

            setOptionToAdd((prev) => ({
              type: prev.type, // Preserve the type (self-serve/manual) when resetting
              role: ApplicantType.Applicant
            }));
          }}
        >
          <legend className="text-sm text-content-700 font-medium mb-2">
            Add person
          </legend>

          <div className="flex gap-2">
            {optionToAdd.type === "self-serve" ? (
              <div className="w-full">
                <Input
                  placeholder="Enter email"
                  value={optionToAdd.email}
                  onChange={(e) =>
                    setOptionToAdd({ ...optionToAdd, email: e.target.value })
                  }
                  id="email"
                />
              </div>
            ) : (
              <div className="flex w-full">
                <div className="w-full">
                  <Input
                    className="rounded-r-none rounded-l-lg"
                    id="first_name"
                    placeholder="Enter first name"
                    value={optionToAdd.first_name}
                    onChange={(e) =>
                      setOptionToAdd({
                        ...optionToAdd,
                        first_name: e.target.value
                      })
                    }
                  />
                </div>
                <div className="w-full">
                  <Input
                    className="rounded-l-none rounded-r-lg"
                    id="last_name"
                    placeholder="Enter last name"
                    value={optionToAdd.last_name}
                    onChange={(e) =>
                      setOptionToAdd({
                        ...optionToAdd,
                        last_name: e.target.value
                      })
                    }
                  />
                </div>
              </div>
            )}

            <Show
              if={
                optionToAdd.type === "manual"
                  ? optionToAdd.first_name && optionToAdd.last_name
                  : optionToAdd.email
              }
            >
              <div>
                <RoleSelect
                  className="w-[195px]"
                  value={optionToAdd.role}
                  onChange={(role) => setOptionToAdd({ ...optionToAdd, role })}
                  isDisabled={false}
                  disabledRoles={disabledRoles}
                  template={template}
                />
              </div>
            </Show>

            <Button
              type="submit"
              variant={ButtonVariant.PRIMARY}
              className="shrink-0"
            >
              Add
            </Button>
          </div>
        </form>

        {options.length ? (
          <div>
            <h3 className="text-sm text-content-300 font-medium mb-4">
              Added people
            </h3>
            <div className="max-h-[200px] h-full overflow-y-auto">
              {options.map((option, idx) => {
                return (
                  <ApplicantToAddRow
                    key={`${idx}-${option.role}-${option.type}-${
                      option.type === "self-serve"
                        ? option.email
                        : `${option.first_name}${option.last_name}`
                    }`}
                    option={option}
                    onRemove={() => {
                      setOptions(options.filter((_, i) => i !== idx));
                    }}
                    options={options}
                    updateOption={(option) => {
                      setOptions(
                        options.map((o, i) => (i === idx ? option : o))
                      );
                    }}
                    template={template}
                  />
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex justify-between border-t border-alpha-40 pt-4">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={handleCopyClick}
            disabled={!unit?.magic_link?.active}
            tooltip={
              !unit?.magic_link?.active
                ? `When a ${dictionary.unit} is not accepting applications, applicants can only be invited via email`
                : undefined
            }
            tooltipZIndex={50}
            tooltipPlacement="bottom"
          >
            Copy application link
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            disabled={!options.length}
            onClick={() => handleInvite(options)}
            loading={isInviteLoading}
          >
            Invite applicants
          </Button>
        </div>
      </div>
    </>
  );
};
